// WHAT THIS CLIENT COSTS US — the third tab on the client card.
//
// WHY IT LIVES HERE AND NOT ON THE MONEY SCREEN. The money screen is scanned ACROSS
// clients and deliberately has no client level (see CollectionsBoard); this asks one
// question about one client, over the whole life of the relationship. It is the client
// card's own question, and the card is where you already are when you ask it.
//
// THE FIGURE IT LEADS WITH is the absorbed cost: everything we spent on this client and
// charged to nobody. That is the money that came out of our profit, and it is what
// "is this client worth it" actually turns on — a client with large costs that are all
// charged on is not expensive, and one with small costs that we swallow every time is.
//
// THE THREE BUCKETS ARE NEVER ADDED TOGETHER. Absorbed is money gone; charged is money
// laid out and recovered; unstated is money we cannot honestly classify because the row
// predates the question (migration 054). Summing them would produce a "total cost" that
// is true of nothing. The unstated line is shown whenever it is non-zero, and shown as
// unknown rather than quietly counted as absorbed — that conflation is the exact reason
// `borne_by` is three-valued and not a boolean.
//
// NOTHING HERE IS COMPUTED IN THE BROWSER. Every figure arrives from the server (see
// api/rest/clients/costs.js), which is the thin-client rule and also what keeps this
// screen from becoming a second opinion about money.
import useAsync from '../../hooks/useAsync';
import { clientsApi } from '../../api';
import he from '../../i18n/he';
import AsyncBoundary from '../../components/Loader';
import EmptyState from '../../components/EmptyState';
import StatTile from '../../components/StatTile';
import Money, { Cost, Profit } from '../../components/Money';
import UnitCostRows from './UnitCostRows';

const t = he.clientCard.costs;

// A figure worth drawing attention to only when it is not zero. Guarded against float
// dust the same way the server's money comparisons are.
const any = (n) => Number(n) > 0.005;

export default function CostsTab({ clientId }) {
  const { data, loading, error } = useAsync(() => clientsApi.costs(clientId), [clientId]);
  const extras = data && (any(data.charged.awaitingCost) || any(data.charged.awaitingPlanned)
    || any(data.unstated) || data.charged.cancelledCharges > 0);

  return (
    <AsyncBoundary loading={loading} error={error}>
      {data && (
        <>
          <div className="client-cost-stats">
            {/* The headline, and the only one of the three that is a cost to us. */}
            <StatTile
              label={t.absorbed}
              value={<Cost value={data.absorbed} />}
            />
            {/* Laid out and recovered — reported as the pair, so the margin can be read
                rather than taken on trust. */}
            <StatTile
              label={t.chargedCost}
              value={<Cost value={data.charged.cost} />}
              note={(
                <span className="muted small-note">
                  {t.invoicedFor} <Cost value={data.charged.resolvedCost} />:{' '}
                  <Money value={data.charged.invoiced} />
                </span>
              )}
            />
            <StatTile
              label={t.margin}
              tone={data.charged.margin < 0 ? 'danger' : ''}
              value={<Profit value={data.charged.margin} />}
            />
          </div>

          {/* The qualifications, each drawn only when it applies — and the block with them.
              A line always on screen saying "0" is a line nobody reads. */}
          {extras && (
            <div className="cost-extras">
              {/* EITHER figure, not just the cost. A cost recorded at 0 with a planned
                  charge of 1,000 is a real row with a real intent, and gating on the cost
                  alone hid that 1,000 completely — the tab held the figure and refused to
                  show it, which is how a data-entry slip became an invisible bug. */}
              {(any(data.charged.awaitingCost) || any(data.charged.awaitingPlanned)) && (
                <p>
                  {t.awaiting}: <strong><Cost value={data.charged.awaitingCost} /></strong>
                  {' · '}
                  {t.awaitingPlanned}:{' '}
                  <Money value={data.charged.awaitingPlanned} />
                  {data.charged.plannedMissing > 0 && (
                    <span className="muted"> ({t.plannedMissing(data.charged.plannedMissing)})</span>
                  )}
                </p>
              )}
              {/* Never folded into absorbed. We do not know which it was. */}
              {any(data.unstated) && (
                <p>
                  {t.unstated}: <strong><Cost value={data.unstated} /></strong>
                </p>
              )}
              {/* A charge was raised and then cancelled: neither recovered nor waiting.
                  Named rather than dropped into whichever bucket was nearest. */}
              {data.charged.cancelledCharges > 0 && (
                <p className="muted">
                  {t.cancelledCharges(data.charged.cancelledCharges)}{' '}
                  <Cost value={data.charged.cancelledCost} />
                </p>
              )}
            </div>
          )}

          <div className="card">
            <h2>{t.byUnitTitle}</h2>
            {data.units.length > 0
              ? <UnitCostRows units={data.units} />
              : <EmptyState text={t.empty} />}
          </div>
        </>
      )}
    </AsyncBoundary>
  );
}
